/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ActionsService, OnePassService } from "@tivocorp/data-domain-lib";
import type {
  OnePassOptions,
  OnePassValues,
  OnePassValuesWithDisplayOptions
} from "@tivocorp/data-domain-lib/client-code/onepass";

import type { IOnePassInteractor } from "../../interfaces";
import type { TAnalyticsFields, TNetworkOptions } from "../../types";
import { AnalyticsLogger, Log } from "../../logging";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { clearServiceCache } from "./serviceCache";

/**
 * Facade for OnePass (Series Recording) operations. Resolves the
 * `cloudcore-onepass` endpoint via TSRI, builds authenticated params and
 * runs every call through the NetworkGuard layer.
 */
export class OnePassInteractor
  extends BaseInteractor
  implements IOnePassInteractor
{
  private static readonly TAG: string = "OnePassInteractor";

  private async buildService(): Promise<OnePassService> {
    const params = await this.buildServicesParams(
      TIVO_SERVICES.OnePassService,
      ApplicationFeatureArea.MY_SHOWS
    );
    return new OnePassService(params);
  }

  public async fetchSchedule(
    ucid: string,
    options?: TNetworkOptions
  ): Promise<OnePassValuesWithDisplayOptions> {
    return this.executeGuarded(
      "fetchSchedule",
      async (
        guardedSignal: AbortSignal
      ): Promise<OnePassValuesWithDisplayOptions> => {
        Log.d(OnePassInteractor.TAG, "fetchSchedule() called");
        const service: OnePassService = await this.buildService();
        return service.fetchSchedule({ ucid }, guardedSignal);
      },
      options
    );
  }

  public async fetchOptions(
    ucid: string,
    options?: TNetworkOptions
  ): Promise<OnePassOptions> {
    return this.executeGuarded(
      "fetchOptions",
      async (guardedSignal: AbortSignal): Promise<OnePassOptions> => {
        Log.d(OnePassInteractor.TAG, "fetchOptions() called");
        const service: OnePassService = await this.buildService();
        return service.fetchOptions({ ucid }, guardedSignal);
      },
      options
    );
  }

  /**
   * Creates a series recording (OnePass) and fires the `createOnePass`
   * analytics event on success — instrumented here rather than at each call
   * site so every screen that creates a OnePass through this interactor
   * (My Hub, content details, …) reports it identically.
   */
  public async createOnePass(
    ucid: string,
    onePassValues: OnePassValues,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "createOnePass",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(OnePassInteractor.TAG, "createOnePass() called");
        const service: OnePassService = await this.buildService();
        await service.createOnePass({ ucid, onePassValues }, guardedSignal);
        clearServiceCache(ActionsService, OnePassInteractor.TAG);
        Log.i(OnePassInteractor.TAG, `createOnePass() succeeded ucid=${ucid}`);
        AnalyticsLogger.logCreateOnePass({
          collection_id: ucid,
          ...analyticsFields
        });
      },
      options
    );
  }

  /**
   * Updates an existing series recording (OnePass) and fires the
   * `modifyOnePass` analytics event on success — see {@link createOnePass}.
   */
  public async updateOnePass(
    ucid: string,
    onePassValues: OnePassValues,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "updateOnePass",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(OnePassInteractor.TAG, "updateOnePass() called");
        const service: OnePassService = await this.buildService();
        await service.updateOnePass({ ucid, onePassValues }, guardedSignal);
        clearServiceCache(ActionsService, OnePassInteractor.TAG);
        Log.i(OnePassInteractor.TAG, `updateOnePass() succeeded ucid=${ucid}`);
        AnalyticsLogger.logModifyOnePass({
          collection_id: ucid,
          ...analyticsFields
        });
      },
      options
    );
  }

  /**
   * Cancels a series recording's (OnePass) schedule and fires the
   * `cancelOnePass` analytics event on success — see {@link createOnePass}.
   */
  public async cancelSchedule(
    ucid: string,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "cancelSchedule",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(OnePassInteractor.TAG, "cancelSchedule() called");
        const service: OnePassService = await this.buildService();
        await service.cancelSchedule({ ucid }, guardedSignal);
        clearServiceCache(ActionsService, OnePassInteractor.TAG);
        Log.i(OnePassInteractor.TAG, `cancelSchedule() succeeded ucid=${ucid}`);
        AnalyticsLogger.logCancelOnePass({
          collection_id: ucid,
          ...analyticsFields
        });
      },
      options
    );
  }
}
